import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  notifyTaskReviewChanged, showTaskReview, subscribeTaskReview, subscribeTaskReviewChanged,
} from "@/lib/taskReview";

/* "You still have N open task(s) for this customer" (Workstream J).

   NON-BLOCKING by construction: a fixed, dismissible panel - never a modal, no
   overlay, nothing traps the employee. Closing it leaves every task open and the
   reminder stays in the notification bell (and on the customer's Tasks tab) until it is
   resolved. The five actions per task: Complete (only where the backend says it can be
   truthfully confirmed), No longer applicable (reason required), Keep open, Reschedule,
   and Open customer tasks.

   TaskReviewHost is mounted ONCE (AppShell); outcome dialogs raise it through
   lib/taskReview.maybeShowTaskReview so it survives their unmounting. */

const seedFrom = (p) => ({
  review_id: p.review_id,
  title: p.title || `You still have ${p.unresolved_count ?? (p.tasks || []).length} open task(s) for this customer`,
  unresolved_count: p.unresolved_count ?? (p.tasks || []).length,
  customer_name: p.customer_name || null,
  customer_tasks_link: p.customer_tasks_link || (p.contact_id ? `/customers/${p.contact_id}?tab=tasks` : null),
  loading: !p.tasks,
  tasks: (p.tasks || []).map((t) => ({ ...t, reviewed: t.reviewed === true, can_complete: t.can_complete === true })),
});

const RELEVANCE_LABEL = { may_be_obsolete: "May now be obsolete", still_required: "Still relevant" };

export const TaskReviewRow = ({ task, busy, onAction, onOpenTasks }) => {
  const [form, setForm] = useState(null); // null | "complete" | "obsolete" | "reschedule"
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const id = task.task_id;
  const reset = () => { setForm(null); setText(""); setDue(""); };
  const informational = task.complete_mode === "informational_acknowledgement";
  const submit = async (action, extra) => {
    const ok = await onAction(task, action, extra);
    if (ok) reset();
  };
  return (
    <div className={`rounded-md border p-2 space-y-1.5 ${task.reviewed ? "opacity-60" : ""}`} data-testid={`task-review-task-${id}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-sm font-medium flex-1 min-w-32">{task.title}</span>
        <Badge variant="outline" className="text-[10px] capitalize">{task.source}</Badge>
        {task.reviewed && <Badge variant="outline" className="text-[10px]">Reviewed - kept open</Badge>}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Due {task.due_at ? fmtIST(task.due_at, { time: false }) : "-"} · {task.purpose_label || task.purpose}
      </p>
      <p className={`text-[11px] ${task.relevance === "may_be_obsolete" ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`}
        data-testid={`task-review-why-${id}`}>
        <b>{RELEVANCE_LABEL[task.relevance] || "Open"}:</b> {(task.why || "").replace(/^(May now be obsolete|Still relevant): /, "")}
      </p>
      {!task.reviewed && form === null && (
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy || !task.can_complete}
            title={task.can_complete ? "Confirm you actually did this" : (task.complete_note || "Cannot be completed from here")}
            data-testid={`task-review-complete-${id}`}
            onClick={() => (informational ? setForm("complete") : submit("complete", {}))}>Complete</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
            data-testid={`task-review-obsolete-${id}`} onClick={() => setForm("obsolete")}>No longer applicable</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
            data-testid={`task-review-keep-${id}`} onClick={() => submit("keep_open", {})}>Keep open</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
            data-testid={`task-review-reschedule-${id}`} onClick={() => setForm("reschedule")}>Reschedule</Button>
        </div>
      )}
      {!task.reviewed && !task.can_complete && task.complete_note && form === null && (
        <p className="text-[10px] text-muted-foreground">Complete: {task.complete_note}</p>
      )}
      {form === "complete" && (
        <div className="space-y-1.5">
          <Textarea data-testid={`task-review-complete-note-${id}`} placeholder="What was actually done? (required)" value={text}
            onChange={(e) => setText(e.target.value)} className="min-h-14 text-xs" />
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 text-xs" disabled={busy || !text.trim()} data-testid={`task-review-complete-confirm-${id}`}
              onClick={() => submit("complete", { note: text.trim() })}>Confirm complete</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={reset}>Cancel</Button>
          </div>
        </div>
      )}
      {form === "obsolete" && (
        <div className="space-y-1.5">
          <Input data-testid={`task-review-obsolete-reason-${id}`} placeholder="Why is this no longer applicable? (required)" value={text}
            onChange={(e) => setText(e.target.value)} className="h-8 text-xs" />
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 text-xs" disabled={busy || text.trim().length < 3}
              data-testid={`task-review-obsolete-confirm-${id}`} onClick={() => submit("no_longer_applicable", { reason: text.trim() })}>
              Mark no longer applicable</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={reset}>Cancel</Button>
          </div>
        </div>
      )}
      {form === "reschedule" && (
        <div className="space-y-1.5">
          <Input data-testid={`task-review-reschedule-date-${id}`} type="datetime-local" value={due}
            onChange={(e) => setDue(e.target.value)} className="h-8 text-xs" />
          <Input data-testid={`task-review-reschedule-reason-${id}`} placeholder="Reason (needed if this is not your own task)"
            value={text} onChange={(e) => setText(e.target.value)} className="h-8 text-xs" />
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 text-xs" disabled={busy || !due} data-testid={`task-review-reschedule-confirm-${id}`}
              onClick={() => submit("reschedule", { due_at: new Date(due).toISOString(), reason: text.trim() || null })}>Reschedule</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={reset}>Cancel</Button>
          </div>
        </div>
      )}
      <Button size="sm" variant="link" className="h-5 px-0 text-[11px]" data-testid={`task-review-open-${id}`}
        onClick={onOpenTasks}>Open customer tasks</Button>
    </div>
  );
};

export const TaskReviewPanel = ({ review, onClose, onAction, busyId, onOpenTasks }) => {
  const pending = review.tasks.filter((t) => !t.reviewed);
  const title = review.loading ? "Open tasks for this customer"
    : `You still have ${pending.length} open task(s) for this customer`;
  return (
    <div role="region" aria-label="Open tasks for this customer" data-testid="task-review-panel"
      className="fixed bottom-4 right-4 z-50 w-[26rem] max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto rounded-lg border bg-card shadow-lg p-3 space-y-2">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" data-testid="task-review-title">{title}</p>
          {review.customer_name && <p className="text-[11px] text-muted-foreground truncate">{review.customer_name}</p>}
        </div>
        <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" aria-label="Dismiss - tasks stay open"
          data-testid="task-review-dismiss" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Your outcome is saved. Closing this leaves every task open - the reminder stays in your notifications.
      </p>
      {review.loading && <p className="text-xs text-muted-foreground" data-testid="task-review-loading">Loading…</p>}
      {review.tasks.map((t) => (
        <TaskReviewRow key={t.task_id} task={t} busy={busyId === t.task_id} onAction={onAction} onOpenTasks={onOpenTasks} />
      ))}
      <div className="flex justify-end">
        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="task-review-open-all" onClick={onOpenTasks}>
          Open customer tasks</Button>
      </div>
    </div>
  );
};

export const TaskReviewHost = () => {
  const [review, setReview] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async (reviewId) => {
    try {
      const { data } = await api.get(`/task-review/${reviewId}`);
      setReview((cur) => (cur && cur.review_id !== reviewId ? cur : { ...seedFrom(data), tasks: data.tasks }));
      if (!data.unresolved_count) setReview(null);
    } catch { /* the seeded list stays usable; a failed refresh is not fatal */ }
  }, []);

  useEffect(() => subscribeTaskReview((payload) => {
    setReview(seedFrom(payload));
    if (payload.review_id) load(payload.review_id);
  }), [load]);

  const onAction = async (task, action, extra) => {
    setBusyId(task.task_id);
    try {
      const { data } = await api.post(`/task-review/${review.review_id}/tasks/${task.task_id}/action`, { action, ...extra });
      notifyTaskReviewChanged();
      const next = data.review;
      if (!next || !next.unresolved_count) {
        toast.success("All open tasks reviewed");
        setReview(null);
      } else {
        setReview({ ...seedFrom(next), tasks: next.tasks });
      }
      return true;
    } catch (e) {
      toast.error(errDetail(e));
      return false;
    } finally { setBusyId(null); }
  };

  const openTasks = () => {
    const link = review && review.customer_tasks_link;
    setReview(null);
    if (link) navigate(link);
  };

  if (!review) return null;
  return <TaskReviewPanel review={review} busyId={busyId} onAction={onAction} onClose={() => setReview(null)} onOpenTasks={openTasks} />;
};

/* Customer 360 - the reminder stays available on the customer's own Tasks tab. */
export const TaskReviewBanner = ({ contactId, onChanged }) => {
  const [review, setReview] = useState(null);
  const load = useCallback(() => {
    api.get("/task-review/pending", { params: { contact_id: contactId } })
      .then((r) => setReview(r.data.review || null)).catch(() => setReview(null));
  }, [contactId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => subscribeTaskReviewChanged(() => { load(); if (onChanged) onChanged(); }), [load, onChanged]);
  if (!review) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-2 mb-2"
      data-testid="task-review-banner">
      <span className="text-sm flex-1 min-w-40">You still have {review.unresolved_count} open task(s) for this customer that may need your review.</span>
      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="task-review-banner-open"
        onClick={() => showTaskReview({ review_id: review.review_id })}>Review now</Button>
    </div>
  );
};
